/**
 * Raw → cooked engine.
 *
 * Principle (the user's rule): the RAW nutrient profile is the truth. Cooking
 * mostly changes WEIGHT (water leaves meat; water enters rice and pasta), so a
 * cooked portion is converted back to the raw amount it came from:
 *
 *     raw grams = cooked grams / yield        yield = cooked weight / raw weight
 *
 * Water follows the weight change (what isn't fat is water). The one other
 * change we model is FAT that leaves the food (drips off a
 * grill, drained from the pan, rinsed away). That removes fat grams and 9 kcal
 * per gram, and is capped so calories never drop more than 20% (a deliberate
 * safety margin for imperfect draining).
 *
 * Sources
 *  - Yields and fat change for meat and poultry: USDA Table of Cooking Yields
 *    for Meat and Poultry, Release 2 (2014), see usdaYields.ts.
 *  - Rinsing cooked ground beef crumbles with hot water significantly lowers
 *    fat while keeping protein, iron, zinc and B12: Love JA, Prusa KJ.
 *    J Am Diet Assoc. 1992;92(11):1367-71. PMID 1430722. Iowa State reported
 *    "up to 50%" less fat, used here as the ceiling for drained + rinsed.
 *  - Fish, grain and legume yields: derived from USDA SR Legacy raw/cooked
 *    pairs of the same food (protein for fish, carbohydrate for grains and
 *    legumes are retained through cooking, so the concentration ratio equals
 *    the weight ratio). Pairs listed in DERIVED_YIELDS.
 *  - Portion-estimate error without a scale: Gibson AA et al. Accuracy of
 *    hands v. household measures as portion size estimation aids. J Nutr Sci.
 *    2016;5:e29. doi:10.1017/jns.2016.22 (only 29% of household-measure
 *    estimates were within ±25% of the true weight).
 *  - Cooking fat ADDED to the dish (oil, butter) is the other thing that changes
 *    a batch's calories: its energy goes in with it. Values per 100 g are USDA
 *    FoodData Central SR Legacy (see COOKING_FATS). How much oil a food keeps is
 *    the cook's number to enter; for scale, French fries end up at about 13 g of
 *    oil per 100 g (Ziaiifar AM et al. Int J Food Sci Technol. 2008;43(8):1410-23,
 *    doi:10.1111/j.1365-2621.2007.01664.x).
 */
import { NUTRIENTS, roundNutrient, type NutrientMap } from '../nutrition/catalog';
import { USDA_COOKING_YIELDS, type UsdaYieldRow } from './usdaYields';

export const MAX_CALORIE_REDUCTION: number = 0.2;

export type FoodKind = 'ground_meat' | 'beef' | 'pork' | 'poultry' | 'lamb_game' | 'sausage' | 'fish' | 'grain' | 'legume' | 'other';
export type CookMethod = 'grilled' | 'roasted' | 'pan_fried' | 'pan_broiled' | 'braised' | 'simmered' | 'deep_fried' | 'dry_heat' | 'boiled';
export type FatHandling = 'kept' | 'drained' | 'drained_rinsed' | 'rack';
export type AmountMethod = 'scale' | 'package' | 'eyeball';

export const METHOD_LABEL: Readonly<Record<CookMethod, string>> = {
  grilled: 'Grilled or broiled',
  roasted: 'Baked or roasted',
  pan_fried: 'Pan-fried / browned in a skillet',
  pan_broiled: 'Pan-broiled (skillet, no oil)',
  braised: 'Braised (covered, with liquid)',
  simmered: 'Simmered, poached or stewed',
  deep_fried: 'Deep-fried',
  dry_heat: 'Baked, grilled or pan-seared',
  boiled: 'Boiled / cooked in water',
};

export const FAT_HANDLING_LABEL: Readonly<Record<FatHandling, string>> = {
  kept: 'Fat stays in the food or dish (not drained)',
  drained: 'Fat drained off / left in the pan',
  drained_rinsed: 'Drained, then rinsed with hot water',
  rack: 'On a rack or grill so fat drips away',
};

export const AMOUNT_METHODS: ReadonlyArray<{ id: AmountMethod; label: string; relError: number; basis: string }> = [
  { id: 'scale', label: 'Weighed on a kitchen scale', relError: 0.02, basis: 'Kitchen scales read to about 1 g; ±2% allows for tare and reading error.' },
  { id: 'package', label: 'From the package label (e.g. a 1 lb pack)', relError: 0.05, basis: 'Net weight on US labels is legally checked (NIST Handbook 133). Splitting it into portions adds your own error, so ±5% is assumed.' },
  { id: 'eyeball', label: 'Eyeballed (cups, pieces, "about 4 oz")', relError: 0.3, basis: 'Gibson et al. 2016: only 29% of estimates made with household measures were within ±25% of the true weight, so expect ±30% or more.' },
];

// ---------------------------------------------------------------------------
// Cooking fats (USDA FoodData Central, SR Legacy, per 100 g; tablespoon weights from the same entries)
// ---------------------------------------------------------------------------
export type CookingFatId = 'olive_oil' | 'canola_oil' | 'butter' | 'lard' | 'coconut_oil';

export const COOKING_FATS: Readonly<Record<CookingFatId, { name: string; fdcId: number; tbspG: number; per100: NutrientMap }>> = {
  olive_oil: { name: 'Olive oil', fdcId: 171413, tbspG: 13.5, per100: { energy_kcal: 884, fat_g: 100, saturated_g: 13.8 } },
  canola_oil: { name: 'Canola or vegetable oil', fdcId: 172336, tbspG: 14.0, per100: { energy_kcal: 884, fat_g: 100, saturated_g: 7.36 } },
  butter: { name: 'Butter, salted', fdcId: 173410, tbspG: 14.2, per100: { energy_kcal: 717, fat_g: 81.1, saturated_g: 51.4 } },
  lard: { name: 'Lard', fdcId: 171401, tbspG: 12.8, per100: { energy_kcal: 902, fat_g: 100, saturated_g: 39.2 } },
  coconut_oil: { name: 'Coconut oil', fdcId: 171412, tbspG: 13.6, per100: { energy_kcal: 892, fat_g: 99.1, saturated_g: 82.5 } },
};

/** Cooking fat that ends up in the food (what was used minus what stayed in the pan). */
export interface AddedFat {
  fat: CookingFatId;
  grams: number;
}

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------
const CATEGORY_KIND: Readonly<Record<string, FoodKind>> = {
  'Beef Products': 'beef',
  'Pork Products': 'pork',
  'Poultry Products': 'poultry',
  'Lamb, Veal, and Game Products': 'lamb_game',
  'Sausages and Luncheon Meats': 'sausage',
  'Finfish and Shellfish Products': 'fish',
  'Cereal Grains and Pasta': 'grain',
  'Legumes and Legume Products': 'legume',
};

const MEAT_KINDS: ReadonlySet<FoodKind> = new Set<FoodKind>(['ground_meat', 'beef', 'pork', 'poultry', 'lamb_game', 'sausage']);

export function classifyFood(name: string, category: string | null): FoodKind {
  const n: string = name.toLowerCase();
  let kind: FoodKind | undefined = category ? CATEGORY_KIND[category] : undefined;
  if (!kind) {
    if (/\b(beef|steak|brisket|sirloin)\b/.test(n)) kind = 'beef';
    else if (/\b(pork|ham)\b/.test(n)) kind = 'pork';
    else if (/\b(chicken|turkey|duck|poultry)\b/.test(n)) kind = 'poultry';
    else if (/\b(lamb|veal|bison|venison|deer|elk|goat)\b/.test(n)) kind = 'lamb_game';
    else if (/\b(salmon|tuna|cod|tilapia|shrimp|fish|halibut|trout|catfish)\b/.test(n)) kind = 'fish';
    else if (/\b(rice|pasta|spaghetti|macaroni|quinoa|oats|oatmeal|barley|couscous)\b/.test(n)) kind = 'grain';
    else if (/\b(lentils?|beans?|chickpeas?|garbanzo|split peas)\b/.test(n)) kind = 'legume';
    else kind = 'other';
  }
  if ((kind === 'beef' || kind === 'pork' || kind === 'poultry' || kind === 'lamb_game') && /\bground\b|\bburger|\bpatt(y|ies)\b/.test(n)) return 'ground_meat';
  return kind;
}

export function methodsFor(kind: FoodKind): CookMethod[] {
  switch (kind) {
    case 'ground_meat': return ['pan_fried', 'grilled', 'pan_broiled', 'roasted'];
    case 'beef': case 'lamb_game': return ['grilled', 'roasted', 'pan_fried', 'braised', 'simmered'];
    case 'pork': return ['roasted', 'grilled', 'pan_fried', 'pan_broiled', 'braised'];
    case 'poultry': return ['roasted', 'grilled', 'pan_fried', 'braised', 'simmered', 'deep_fried'];
    case 'sausage': return ['grilled', 'pan_fried'];
    case 'fish': return ['dry_heat'];
    case 'grain': case 'legume': return ['boiled'];
    case 'other': return [];
  }
}

export function defaultFatHandling(method: CookMethod): FatHandling {
  if (method === 'grilled') return 'rack';
  if (method === 'pan_fried' || method === 'pan_broiled' || method === 'roasted') return 'drained';
  return 'kept';
}

export function usesFatHandling(kind: FoodKind): boolean { return MEAT_KINDS.has(kind); }

// ---------------------------------------------------------------------------
// Yields
// ---------------------------------------------------------------------------
export interface YieldEstimate {
  /** cooked weight / raw weight, e.g. 0.73 */
  yield: number;
  low: number;
  high: number;
  /** Human-readable provenance shown in the UI. */
  basis: string;
  source: 'measured' | 'usda_table' | 'usda_table_category' | 'usda_sr_pairs';
  /** The USDA table row used, when there was a specific match. */
  row?: UsdaYieldRow;
}

const GROUP_OF: Partial<Record<FoodKind, number>> = { beef: 13, pork: 10, poultry: 5, lamb_game: 17, sausage: 7 };

/** Derived from SR Legacy raw/cooked pairs (see file header). */
export const DERIVED_YIELDS: ReadonlyArray<{ match: RegExp; kind: FoodKind; yield: number; pair: string }> = [
  { match: /\brice\b.*\bbrown\b|\bbrown\b.*\brice\b/, kind: 'grain', yield: 2.98, pair: 'Rice, brown, long-grain: raw → cooked (carbohydrate 76.25 → 25.58 g/100 g)' },
  { match: /\brice\b/, kind: 'grain', yield: 2.84, pair: 'Rice, white, long-grain, enriched: raw → cooked (79.95 → 28.17 g/100 g)' },
  { match: /\b(pasta|spaghetti|macaroni|noodles?)\b/, kind: 'grain', yield: 2.42, pair: 'Pasta, enriched: dry → cooked (74.67 → 30.86 g/100 g)' },
  { match: /\bquinoa\b/, kind: 'grain', yield: 3.01, pair: 'Quinoa: uncooked → cooked (64.16 → 21.30 g/100 g)' },
  { match: /\boats?\b|\boatmeal\b/, kind: 'grain', yield: 5.64, pair: 'Oats, regular/quick: dry → cooked with water (67.7 → 12.0 g/100 g). Depends a lot on how much water you use: weigh if you can.' },
  { match: /\blentils?\b/, kind: 'legume', yield: 3.15, pair: 'Lentils: raw → boiled (63.35 → 20.13 g/100 g)' },
  { match: /\bchickpeas?\b|\bgarbanzo\b/, kind: 'legume', yield: 2.30, pair: 'Chickpeas: raw → boiled (62.95 → 27.42 g/100 g)' },
  { match: /\bbeans?\b/, kind: 'legume', yield: 2.63, pair: 'Black beans: raw → boiled (62.36 → 23.71 g/100 g)' },
  { match: /\bsalmon\b/, kind: 'fish', yield: 0.92, pair: 'Salmon, Atlantic, farmed: raw → dry heat (protein 20.42 → 22.10 g/100 g)' },
  { match: /\bcod\b/, kind: 'fish', yield: 0.78, pair: 'Cod, Atlantic: raw → dry heat (protein 17.81 → 22.83 g/100 g)' },
  { match: /\btilapia\b/, kind: 'fish', yield: 0.77, pair: 'Tilapia: raw → dry heat (protein 20.08 → 26.15 g/100 g)' },
  { match: /\btuna\b/, kind: 'fish', yield: 0.84, pair: 'Tuna, yellowfin: raw → dry heat (protein 24.40 → 29.15 g/100 g)' },
  { match: /\bshrimp\b/, kind: 'fish', yield: 0.84, pair: 'Shrimp: raw → cooked (protein 20.10 → 23.98 g/100 g)' },
];

/** Fish, dry heat: salmon 0.92, cod 0.78, tilapia 0.77, yellowfin tuna 0.84, shrimp 0.84 (protein ratio, SR Legacy). */
const FISH_YIELD = { yield: 0.84, low: 0.77, high: 0.92 } as const;

// Species words are implied by the food group, so they must not count as a match on their own.
const STOP: ReadonlySet<string> = new Set(['beef', 'pork', 'chicken', 'turkey', 'lamb', 'veal', 'game', 'poultry', 'separable', 'lean', 'and', 'fat', 'trimmed', 'to', 'all', 'grades', 'meat', 'only', 'raw', 'cooked', 'inch', 'external', 'trim', 'choice', 'select', 'prime', 'from', 'the', 'with', 'of', 'in', 'or', 'retail', 'cuts', 'cut', '0', '1', '8', 'boneless', 'bone', 'broilers', 'fryers', 'broiler', 'fryer', 'enhanced', 'non', 'america', 's']);

function tokens(s: string): Set<string> {
  return new Set(s.toLowerCase().split(/[^a-z0-9]+/).filter((t) => t && !STOP.has(t)));
}

function median(xs: number[]): number {
  const s: number[] = [...xs].sort((a, b) => a - b);
  const m: number = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

function fromRow(row: UsdaYieldRow, source: YieldEstimate['source'], basis: string): YieldEstimate {
  const y: number = row.y / 100;
  return {
    yield: y,
    low: (row.lo ?? row.y * 0.93) / 100,
    high: (row.hi ?? row.y * 1.07) / 100,
    basis, source, row,
  };
}

/** Tier of a ground-meat row for the raw fat %, using USDA's label tiers. */
function groundTier(species: 'beef' | 'pork', fatPct: number): string {
  if (species === 'beef') return fatPct < 12 ? 'low fat' : fatPct <= 22 ? 'medium fat' : 'high fat';
  return fatPct < 10 ? 'low fat' : fatPct <= 27 ? 'medium fat' : 'high fat';
}

function groundSpecies(name: string): 'beef' | 'pork' | 'bison' | 'deer' | 'elk' | 'other' {
  const n: string = name.toLowerCase();
  if (/\bbison|buffalo\b/.test(n)) return 'bison';
  if (/\bdeer|venison\b/.test(n)) return 'deer';
  if (/\belk\b/.test(n)) return 'elk';
  if (/\bpork\b/.test(n)) return 'pork';
  if (/\bbeef|hamburger|burger\b/.test(n)) return 'beef';
  return 'other';
}

function groundRowMethod(method: CookMethod): { m: string; shape: string } {
  // USDA cooked ground meat as crumbles (pan-fried/browned), patties (broiled/grilled or pan-broiled) and loaves (baked).
  if (method === 'pan_fried') return { m: 'pan_fried', shape: 'crumbles' };
  if (method === 'pan_broiled') return { m: 'pan_broiled', shape: 'patty' };
  if (method === 'roasted') return { m: 'roasted', shape: 'loaf' };
  return { m: 'grilled', shape: 'patty' };
}

/**
 * Typical cooked/raw weight ratio for a food and method, with a range.
 * Returns null when there is no published basis (the user should weigh).
 */
export function estimateYield(food: { name: string; category: string | null; fatPer100g: number | null }, method: CookMethod): YieldEstimate | null {
  const kind: FoodKind = classifyFood(food.name, food.category);

  if (kind === 'fish') {
    const specific = DERIVED_YIELDS.find((d) => d.kind === 'fish' && d.match.test(food.name.toLowerCase()));
    if (specific) return { yield: specific.yield, low: specific.yield * 0.93, high: Math.min(1, specific.yield * 1.07), basis: `USDA SR Legacy: ${specific.pair}.`, source: 'usda_sr_pairs' };
    return { ...FISH_YIELD, basis: 'Median of 5 USDA SR Legacy raw/cooked fish pairs (salmon, cod, tilapia, tuna, shrimp); range 77–92%.', source: 'usda_sr_pairs' };
  }
  if (kind === 'grain' || kind === 'legume') {
    const n: string = food.name.toLowerCase();
    const hit = DERIVED_YIELDS.find((d) => d.kind === kind && d.match.test(n));
    if (!hit) return null;
    return { yield: hit.yield, low: hit.yield * 0.9, high: hit.yield * 1.1, basis: `USDA SR Legacy: ${hit.pair}. ±10% for cooking time and water.`, source: 'usda_sr_pairs' };
  }

  if (kind === 'ground_meat') {
    const species = groundSpecies(food.name);
    const { m, shape } = groundRowMethod(method);
    if (species === 'beef' || species === 'pork') {
      const fat: number = food.fatPer100g ?? (species === 'beef' ? 20 : 17);
      const tier: string = groundTier(species, fat);
      const row = USDA_COOKING_YIELDS.find((r) => r.d.toLowerCase().startsWith(`${species}, ground`) && r.d.toLowerCase().includes(tier) && r.d.toLowerCase().includes(shape) && r.m === m)
        ?? USDA_COOKING_YIELDS.find((r) => r.d.toLowerCase().startsWith(`${species}, ground`) && r.d.toLowerCase().includes(tier) && r.m === m)
        ?? USDA_COOKING_YIELDS.find((r) => r.d.toLowerCase().startsWith(`${species}, ground`) && r.d.toLowerCase().includes(tier));
      if (row) return fromRow(row, 'usda_table', `USDA cooking yields: “${row.d}”, ${row.m.replace('_', '-')}${row.n ? `, n=${row.n}` : ''}.`);
    }
    if (species === 'bison' || species === 'deer' || species === 'elk') {
      const row = USDA_COOKING_YIELDS.find((r) => r.d.toLowerCase().includes(species) && r.d.toLowerCase().includes('ground'));
      if (row) return fromRow(row, 'usda_table', `USDA cooking yields: “${row.d}”, grilled${row.n ? `, n=${row.n}` : ''}.`);
    }
    const ground: UsdaYieldRow[] = USDA_COOKING_YIELDS.filter((r) => /\bground\b/i.test(r.d));
    const ys: number[] = ground.map((r) => r.y / 100);
    return { yield: median(ys), low: Math.min(...ys), high: Math.max(...ys), basis: `Median of ${ground.length} USDA ground-meat yields (no specific data for this meat).`, source: 'usda_table_category' };
  }

  const direct: YieldEstimate | null = tableYield(food, kind, method);
  if (direct) return direct;
  // USDA measured some methods only (e.g. no grilled or pan-fried poultry). Rather than fail, use the
  // closest dry-heat method for the same meat, say so, and widen the range.
  for (const proxy of DRY_HEAT_PROXIES[method] ?? []) {
    const p: YieldEstimate | null = tableYield(food, kind, proxy);
    if (!p) continue;
    return {
      ...p,
      low: Math.min(p.low, p.yield * 0.9),
      high: Math.max(p.high, p.yield * 1.1),
      basis: `USDA has no ${METHOD_LABEL[method].toLowerCase()} yield for this meat, so the ${METHOD_LABEL[proxy].toLowerCase()} yield (also dry heat) is used with a wider range; weigh the cooked batch for an exact number. ${p.basis}`,
      source: 'usda_table_category',
    };
  }
  return null;
}

/** Closest dry-heat methods to try, in order, when USDA has no rows for a method. */
const DRY_HEAT_PROXIES: Partial<Record<CookMethod, readonly CookMethod[]>> = {
  pan_fried: ['pan_broiled', 'roasted', 'grilled'],
  pan_broiled: ['pan_fried', 'grilled', 'roasted'],
  grilled: ['roasted', 'pan_broiled', 'pan_fried'],
  roasted: ['grilled', 'pan_broiled', 'pan_fried'],
};

/** Yield from the USDA table for a whole-muscle cut (not ground) and one method, or null. */
function tableYield(food: { name: string }, kind: FoodKind, method: CookMethod): YieldEstimate | null {
  const group: number | undefined = GROUP_OF[kind];
  if (group === undefined) return null;
  const tableMethod: string = method;
  const candidates: UsdaYieldRow[] = USDA_COOKING_YIELDS.filter((r) => r.g === group && r.m === tableMethod && !/\bground\b/i.test(r.d));
  const want: Set<string> = tokens(food.name);
  let best: { row: UsdaYieldRow; score: number } | null = null;
  for (const row of candidates) {
    const rt: Set<string> = tokens(row.d);
    let common: number = 0;
    for (const t of rt) if (want.has(t)) common++;
    const score: number = common === 0 ? 0 : common / Math.max(rt.size, 1);
    if (!best || score > best.score) best = { row, score };
  }
  if (best && best.score >= 0.5 && tokens(best.row.d).size > 0) {
    return fromRow(best.row, 'usda_table', `USDA cooking yields: “${best.row.d}”, ${best.row.m.replace('_', '-')}${best.row.n ? `, n=${best.row.n}` : ''}.`);
  }
  // No close cut: median for this meat group and method, excluding cured/variety meats that skew it.
  const pool: UsdaYieldRow[] = candidates.filter((r) => !/bacon|ham\b|cured|liver|gizzard|heart|kidney|tongue|giblets|neck|back\b/i.test(r.d));
  if (pool.length === 0) return null;
  const ys: number[] = pool.map((r) => r.y / 100);
  return {
    yield: median(ys), low: Math.min(...ys), high: Math.max(...ys),
    basis: `Median of ${pool.length} USDA ${kind} yields for ${METHOD_LABEL[method].toLowerCase()} (no close match for this exact cut).`,
    source: 'usda_table_category',
  };
}

export function measuredYield(rawGrams: number, cookedGrams: number): YieldEstimate {
  const y: number = cookedGrams / rawGrams;
  return { yield: y, low: y, high: y, basis: `Measured: ${Math.round(rawGrams)} g raw → ${Math.round(cookedGrams)} g cooked.`, source: 'measured' };
}

// ---------------------------------------------------------------------------
// Fat that leaves the food
// ---------------------------------------------------------------------------

/** USDA ground beef, grams fat lost per 100 g raw, by raw fat % (tier midpoints 10 / 17.5 / 27 %). */
const BEEF_DRAINED: ReadonlyArray<[number, number]> = [[0, 0], [10, 1.39], [17.5, 5.32], [27, 12.57]]; // pan-fried crumbles, drained in a colander
const BEEF_RACK: ReadonlyArray<[number, number]> = [[0, 0], [10, 1.55], [17.5, 5.17], [27, 11.98]];    // broiled/grilled patties
/** USDA ground pork (tier midpoints 5 / 18.5 / 30 %). Pork loses much less fat than beef. */
const PORK_DRAINED: ReadonlyArray<[number, number]> = [[0, 0], [5, 0.2], [18.5, 1.8], [30, 6.5]];      // pan-browned crumbles
const PORK_RACK: ReadonlyArray<[number, number]> = [[0, 0], [5, 0], [18.5, 1.9], [30, 6.6]];           // broiled/grilled patties

function interpolate(points: ReadonlyArray<[number, number]>, x: number): number {
  if (x <= points[0]![0]) return points[0]![1];
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i]!;
    const [x0, y0] = points[i - 1]!;
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  // Beyond the data: keep the last point's fraction of fat lost.
  const [xl, yl] = points[points.length - 1]!;
  return (yl / xl) * x;
}

export interface FatLoss { gramsPer100gRaw: number; basis: string }

export function fatLoss(food: { name: string; category: string | null; fatPer100g: number | null }, method: CookMethod, handling: FatHandling, estimate: YieldEstimate | null): FatLoss {
  const fat: number = food.fatPer100g ?? 0;
  const kind: FoodKind = classifyFood(food.name, food.category);
  if (!MEAT_KINDS.has(kind) || fat <= 0) return { gramsPer100gRaw: 0, basis: 'No fat loss modelled for this food.' };
  if (handling === 'kept') return { gramsPer100gRaw: 0, basis: 'Fat stays in the dish, so nothing is removed.' };
  if (method === 'braised' || method === 'simmered') return { gramsPer100gRaw: 0, basis: 'Braising/simmering liquid is usually eaten, so no fat is removed.' };

  if (kind === 'ground_meat') {
    const species = groundSpecies(food.name);
    const drained = species === 'pork' ? PORK_DRAINED : BEEF_DRAINED;
    const rack = species === 'pork' ? PORK_RACK : BEEF_RACK;
    const base: number = Math.min(fat, interpolate(handling === 'rack' ? rack : drained, fat));
    const src: string = `USDA cooking yields, ground ${species === 'pork' ? 'pork' : 'beef'} ${handling === 'rack' ? 'broiled/grilled patties' : 'crumbles, drained'} (interpolated for ${roundNutrient(fat)}% raw fat).`;
    if (handling === 'drained_rinsed') {
      const rinsed: number = Math.max(base, fat * 0.5);
      return { gramsPer100gRaw: rinsed, basis: `${src} Rinsing: up to 50% of fat removed (Love & Prusa 1992, PMID 1430722).` };
    }
    return { gramsPer100gRaw: base, basis: src };
  }

  const row: UsdaYieldRow | undefined = estimate?.row;
  if (row && row.fat !== null && row.fat < 0) {
    return { gramsPer100gRaw: Math.min(fat, -row.fat), basis: `USDA cooking yields: ${-row.fat} g fat lost per 100 g raw for “${row.d}”.` };
  }
  return { gramsPer100gRaw: 0, basis: 'USDA reports little or no fat loss for this cut and method.' };
}

// ---------------------------------------------------------------------------
// Cooking a batch
// ---------------------------------------------------------------------------
export interface CookInput {
  food: { name: string; category: string | null };
  /** Raw nutrient profile per 100 g. */
  per100Raw: NutrientMap;
  rawGrams: number;
  method: CookMethod | null;
  handling: FatHandling;
  /** Weighed cooked batch, if the user has it. Overrides the estimated yield. */
  cookedGrams: number | null;
  /** Oil or butter that ends up in the food. Adds its energy; adds its weight unless the batch was weighed. */
  addedFat?: AddedFat | null;
}

export interface CookResult {
  rawGrams: number;
  cookedGrams: number;
  yield: YieldEstimate;
  /** Nutrients in the whole cooked batch. */
  total: NutrientMap;
  /** Nutrients per 100 g of cooked food. */
  per100Cooked: NutrientMap;
  fatRemovedG: number;
  kcalRemoved: number;
  /** Share of the raw calories removed with the fat, 0–0.2. */
  kcalRemovedShare: number;
  /** True when the 20% cap limited the fat removal. */
  capped: boolean;
  /** Cooking fat added to the batch, grams, and its calories. */
  fatAddedG: number;
  kcalAdded: number;
  notes: string[];
}

const FAT_PART_KEYS: readonly string[] = NUTRIENTS.filter((n) => n.group === 'lipids' && n.unit === 'g' && n.key !== 'fat_g').map((n) => n.key);

export function cook(input: CookInput): CookResult | { error: string } {
  if (!(input.rawGrams > 0)) return { error: 'Enter how much raw food you started with.' };
  const fat: number | null = input.per100Raw.fat_g ?? null;
  const foodInfo = { ...input.food, fatPer100g: fat };
  const est: YieldEstimate | null = input.method ? estimateYield(foodInfo, input.method) : null;
  let y: YieldEstimate;
  if (input.cookedGrams !== null && input.cookedGrams > 0) {
    y = measuredYield(input.rawGrams, input.cookedGrams);
    if (est?.row) y = { ...y, row: est.row };
  } else if (est) {
    y = est;
  } else {
    return { error: 'There is no published yield for this food. Weigh the cooked batch to continue.' };
  }
  if (y.yield < 0.15 || y.yield > 8) return { error: 'That cooked weight doesn’t look right for this amount of raw food. Double-check both weights.' };

  const cookedGrams: number = input.rawGrams * y.yield;
  const total: NutrientMap = {};
  for (const [k, v] of Object.entries(input.per100Raw)) total[k] = (v * input.rawGrams) / 100;

  const notes: string[] = [y.basis];
  let fatRemoved: number = 0;
  let capped: boolean = false;
  if (input.method && fat !== null && fat > 0) {
    const loss: FatLoss = fatLoss(foodInfo, input.method, input.handling, est);
    fatRemoved = (loss.gramsPer100gRaw * input.rawGrams) / 100;
    notes.push(loss.basis);
    const rawKcal: number = total.energy_kcal ?? 0;
    if (rawKcal > 0 && fatRemoved * 9 > rawKcal * MAX_CALORIE_REDUCTION) {
      fatRemoved = (rawKcal * MAX_CALORIE_REDUCTION) / 9;
      capped = true;
      notes.push('Calorie reduction capped at 20% to allow for imperfect draining.');
    }
  }

  if (fatRemoved > 0 && total.fat_g !== undefined && total.fat_g > 0) {
    const keep: number = Math.max(0, (total.fat_g - fatRemoved) / total.fat_g);
    total.fat_g = total.fat_g - fatRemoved;
    for (const k of FAT_PART_KEYS) if (total[k] !== undefined) total[k] = total[k]! * keep;
    if (total.energy_kcal !== undefined) total.energy_kcal = total.energy_kcal - fatRemoved * 9;
  }

  // The weight change that isn't fat is water: lost from meat, absorbed by rice/pasta.
  if (total.water_g !== undefined) total.water_g = Math.max(0, total.water_g + (cookedGrams - input.rawGrams) + fatRemoved);

  // Oil or butter that stays in the food adds its energy (and weight, unless the batch was weighed with it).
  let fatAdded: number = 0;
  let kcalAdded: number = 0;
  let finalGrams: number = cookedGrams;
  const added: AddedFat | null | undefined = input.addedFat;
  if (added && added.grams > 0) {
    const f = COOKING_FATS[added.fat];
    fatAdded = added.grams;
    for (const [k, v] of Object.entries(f.per100)) total[k] = (total[k] ?? 0) + (v * added.grams) / 100;
    kcalAdded = (f.per100.energy_kcal! * added.grams) / 100;
    if (!(input.cookedGrams !== null && input.cookedGrams > 0)) finalGrams = cookedGrams + added.grams;
    notes.push(`Added ${Math.round(added.grams * 10) / 10} g of ${f.name.toLowerCase()} (${Math.round(kcalAdded)} kcal; USDA FDC ${f.fdcId}).`);
  }

  const per100Cooked: NutrientMap = {};
  for (const [k, v] of Object.entries(total)) {
    total[k] = roundNutrient(v);
    per100Cooked[k] = roundNutrient((v * 100) / finalGrams);
  }
  const rawKcal: number = ((input.per100Raw.energy_kcal ?? 0) * input.rawGrams) / 100;
  return {
    rawGrams: input.rawGrams,
    cookedGrams: Math.round(finalGrams * 10) / 10,
    yield: y,
    total,
    per100Cooked,
    fatRemovedG: Math.round(fatRemoved * 10) / 10,
    kcalRemoved: Math.round(fatRemoved * 9),
    kcalRemovedShare: rawKcal > 0 ? (fatRemoved * 9) / rawKcal : 0,
    capped,
    fatAddedG: Math.round(fatAdded * 10) / 10,
    kcalAdded: Math.round(kcalAdded),
    notes,
  };
}

/** Raw amount a cooked portion came from, with the yield's range. */
export function rawFromCooked(cookedGrams: number, y: YieldEstimate): { grams: number; low: number; high: number } {
  return { grams: cookedGrams / y.yield, low: cookedGrams / y.high, high: cookedGrams / y.low };
}

/**
 * Relative uncertainty (±) of the calories for a portion, combining how the
 * amount was measured with how the yield was obtained (root-sum-square).
 */
export function portionUncertainty(amount: AmountMethod, y: YieldEstimate | null): number {
  const a: number = AMOUNT_METHODS.find((m) => m.id === amount)?.relError ?? 0.3;
  const yRel: number = y && y.source !== 'measured' ? (y.high - y.low) / 2 / y.yield : 0;
  return Math.sqrt(a * a + yRel * yRel);
}
